import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, RouterModule } from '@angular/router';
import { AgGridModule } from 'ag-grid-angular';
import {
  BehaviorSubject,
  combineLatest,
  finalize,
  map,
  Subject,
  take,
  takeUntil,
  Observable,
} from 'rxjs';

import {
  EOrderTransferStatus,
  IOrderTransfer,
} from 'v2/modules/order/interfaces/order-transfer.interface';
import {
  EOrderTransferRelation,
  OrderTransferApiService,
} from 'v2/modules/shared/services/api/order/order-transfer-api.service';

import {
  TRANSFER_ORDER_ITEMS_GRID_COL_DEFS,
  TRANSFER_ORDER_ITEMS_GRID_OPTIONS,
} from './config/transfer-order-items-ag-grid.config';

@Component({
  selector: 'lum-transfer-order-view',
  templateUrl: './transfer-order-view.component.html',
  styleUrls: ['./transfer-order-view.component.scss'],
  standalone: true,
  imports: [CommonModule, AgGridModule, RouterModule],
})
export class TransferOrderViewComponent implements OnInit, OnDestroy {
  readonly #destroy$ = new Subject<void>();

  #loading = new BehaviorSubject<boolean>(true);
  #error = new BehaviorSubject<string | null>(null);
  #transferOrder = new BehaviorSubject<IOrderTransfer | null>(null);

  vm$ :Observable<{
    canEdit :boolean;
    error :string | null;
    loading :boolean;
    transferOrder :IOrderTransfer | null;
  }> = combineLatest([
    this.#loading.asObservable(),
    this.#error.asObservable(),
    this.#transferOrder.asObservable(),
  ]).pipe(
    map(([loading, error, transferOrder]) => ({
      canEdit: this.#canEdit(transferOrder),
      error,
      loading,
      transferOrder,
    })),
  );

  readonly agColumnDefs = [...TRANSFER_ORDER_ITEMS_GRID_COL_DEFS];
  readonly agGridOptions = { ...TRANSFER_ORDER_ITEMS_GRID_OPTIONS };

  readonly EOrderTransferStatus = EOrderTransferStatus;

  constructor(
    private _route :ActivatedRoute,
    private _orderTransferApi :OrderTransferApiService,
  ) {}

  ngOnInit() :void {
    this._route.params.pipe(takeUntil(this.#destroy$)).subscribe((params) => {
      const id = params['id'];

      if (id) {
        this.#loading.next(true);
        this.#error.next(null);
        this._orderTransferApi
          .getById(Number(id), {
            relations: [
              EOrderTransferRelation.ORDER_ITEMS,
              EOrderTransferRelation.SALES_ORDER,
              EOrderTransferRelation.SUPPLIER_PURCHASE_ORDER,
              EOrderTransferRelation.WAREHOUSE_DESTINATION,
              EOrderTransferRelation.WAREHOUSE_SOURCE,
            ].join('|'),
          })
          .pipe(
            take(1),
            finalize(() => {
              this.#loading.next(false);
            }),
          )
          .subscribe({
            next: (order) => {
              this.#transferOrder.next(order);
            },
            error: (error) => {
              this.#error.next(error.message || 'Failed to load transfer order.');
            },
          });
      } else {
        this.#error.next('No transfer order ID provided.');
        this.#loading.next(false);
      }
    });
  }

  ngOnDestroy() :void {
    this.#destroy$.next();
    this.#destroy$.complete();
  }

  #canEdit(transferOrder :IOrderTransfer) :boolean {
    return (
      transferOrder &&
      transferOrder.orderStatus !== EOrderTransferStatus.POSTED &&
      !transferOrder.supplierPurchaseOrder?.receivingReportRfq
    );
  }
}
